/**
 * VendorGrid Feature Component
 * Manages vendor card grid with scroll animations via IntersectionObserver
 * @component
 */

import React, { useEffect, useRef } from 'react';
import { Vendor } from '../types';
import { VendorCard } from '../components';

interface VendorGridProps {
  vendors: Vendor[];
  onVendorClick: (vendor: Vendor) => void;
}

export const VendorGrid: React.FC<VendorGridProps> = ({ vendors, onVendorClick }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gridRef.current) return;

    const cards = gridRef.current.querySelectorAll('.vendor-card');
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('is-visible');
            }, index * 100); // Stagger animation
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [vendors]);

  if (vendors.length === 0) {
    return (
      <div className="text-center text-gray-400 py-16">
        <p>No vendors found matching your search.</p>
      </div>
    );
  }

  return (
    <div
      ref={gridRef}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
    >
      {vendors.map((vendor) => (
        <VendorCard key={vendor.rank} vendor={vendor} onClick={onVendorClick} />
      ))}
    </div>
  );
};
